/* eslint-disable no-undef */
/* =============================================================================
 * Shared components + helpers for MSync Water Meter Management
 * ===========================================================================*/
import React, { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { DATA } from "./data.js";

// ----------------------------------------------------------------- formatting
const fmtMoney = (n) => "$" + (Math.round(n * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney0 = (n) => "$" + Math.round(n).toLocaleString("en-US");
const fmtNum = (n) => Math.round(n).toLocaleString("en-US");
// Meter reading / usage in HCF (hundred cubic feet). Keeps 1 decimal so small water usage stays legible.
const fmtHcf = (n) => (n == null ? "—" : Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 }));
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtDate = (d) => d ? `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}` : "\u2014";
const fmtDateTime = (d) => d ? `${MON[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}, ${(d.getHours() % 12) || 12}:${String(d.getMinutes()).padStart(2, "0")} ${d.getHours() < 12 ? "AM" : "PM"}` : "\u2014";
const fmtShort = (d) => d ? `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}` : "\u2014";
const fmtMonth = (d) => d ? `${MON[d.getMonth()]} ${d.getFullYear()}` : "\u2014";
function timeAgo(d, now = DATA.NOW) {
  const ms = now - d;
  const h = ms / 3600000;
  if (h < 1) return `${Math.max(1, Math.round(h * 60))}m ago`;
  if (h < 24) return `${Math.round(h)}h ago`;
  const days = Math.round(h / 24);
  return `${days}d ago`;
}

// brand color per manager (derived, harmonious with IC green)
const MGR_COLORS = {
  siligre: "#007F61", greg: "#2A6FDB", guy: "#A0561E", sharon: "#7A4FB0", lily: "#0E8A8A", all: "#393939",
};
const mgrColor = (id) => MGR_COLORS[id] || "#007F61";
function initials(name) {
  const w = name.replace(/[^A-Za-z ]/g, "").trim().split(/\s+/);
  return ((w[0] || "")[0] || "") + ((w[1] || "")[0] || "");
}

// ----------------------------------------------------------------- icons (Lucide)
const ICONS = {
  dashboard: "M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z",
  building: "M3 21h18M5 21V5a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v16M9 7h2M9 11h2M9 15h2M17 21V11a1 1 0 0 1 1-1h1a1 1 0 0 1 1 1v10",
  landmark: "M3 22h18M6 18v-7M10 18v-7M14 18v-7M18 18v-7M12 2L20 7H4Z",
  gauge: "M12 14l3-3M3.34 19a10 10 0 1 1 17.32 0",
  receipt: "M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1zM8 7h8M8 11h8M8 15h5",
  play: "M5 3l14 9-14 9V3z",
  template: "M3 3h18v4H3zM3 11h8v10H3zM15 11h6v10h-6z",
  chart: "M3 3v18h18M7 15V9M12 15V5M17 15v-4",
  sensor: "M12 2a10 10 0 0 0-10 10 10 10 0 0 0 10 10M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10M8 12a4 4 0 0 1 8 0M12 12h.01",
  link: "M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71",
  search: "M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16zM21 21l-4.35-4.35",
  plus: "M12 5v14M5 12h14",
  chevron: "M9 18l6-6-6-6",
  chevronDown: "M6 9l6 6 6-6",
  bell: "M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  external: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3",
  dots: "M12 13a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM12 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2zM12 20a1 1 0 1 0 0-2 1 1 0 0 0 0 2z",
  alert: "M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01",
  droplet: "M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z",
  battery: "M3 7h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2zM23 11v2",
  calendar: "M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z",
  mail: "M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM22 6l-10 7L2 6",
  trend: "M23 6l-9.5 9.5-5-5L1 18M17 6h6v6",
  arrowDown: "M12 5v14M19 12l-7 7-7-7",
  refresh: "M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
  edit: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z",
  trash: "M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6",
  user: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z",
  clock: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zM12 6v6l4 2",
  download: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3",
  filter: "M22 3H2l8 9.46V19l4 2v-8.54L22 3z",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z",
  send: "M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z",
  history: "M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8M12 7v5l4 2",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9",
  power: "M18.36 6.64a9 9 0 1 1-12.73 0M12 2v10",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  moon: "M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z",
  sun: "M12 17a5 5 0 1 0 0-10 5 5 0 0 0 0 10zM12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42",
};
const Icon = ({ name, size = 20, style, className }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {ICONS[name].split("M").filter(Boolean).map((p, i) => <path key={i} d={"M" + p} />)}
  </svg>
);

// ----------------------------------------------------------------- pills
const STATUS_PILL = {
  Paid: ["pill--paid", "Paid"], Open: ["pill--sent", "Open"], Pushed: ["pill--sent", "Pushed"], Sent: ["pill--sent", "Sent"], Overdue: ["pill--overdue", "Overdue"],
  Draft: ["pill--draft", "Draft"], Scheduled: ["pill--scheduled", "Scheduled"],
};
const StatusPill = ({ status }) => {
  const [cls, label] = STATUS_PILL[status] || ["pill--muted", status];
  return <span className={`pill ${cls}`}><span className="pdot" style={{ background: "currentColor" }} />{label}</span>;
};
const Pill = ({ kind, children }) => <span className={`pill pill--${kind}`}>{children}</span>;

// ----------------------------------------------------------------- toggle
const Toggle = ({ on, onChange }) => (
  <button type="button" className={`toggle ${on ? "on" : ""}`} onClick={(e) => { e.stopPropagation(); onChange && onChange(!on); }} aria-pressed={on} />
);

// avatar square
const Avatar = ({ id, label, size = 30, round = 7 }) => (
  <div className="av-sq" style={{ width: size, height: size, borderRadius: round, background: mgrColor(id), fontSize: size * 0.4 }}>
    {label}
  </div>
);

// battery + signal
const Battery = ({ level }) => {
  const color = level < 25 ? "var(--color-danger)" : level < 45 ? "var(--color-warning-text)" : "var(--color-primary)";
  return (
    <span className="batt">
      <span className="batt__bar"><span className="batt__fill" style={{ width: level + "%", background: color }} /></span>
      <span style={{ fontVariantNumeric: "tabular-nums", color, fontWeight: 600, fontSize: 12 }}>{level}%</span>
    </span>
  );
};
// strength is a 0-100 quality percentage; map it onto 5 bars for the glyph.
const Signal = ({ strength }) => {
  const pct = Math.max(0, Math.min(100, Math.round(strength || 0)));
  const bars = Math.min(5, Math.ceil(pct / 20));
  return (
    <span className="signal-row" style={{ display: "inline-flex", alignItems: "center", gap: 7 }} title={`Signal ${pct}%`}>
      <span className="signal">
        {[1, 2, 3, 4, 5].map((i) => <i key={i} className={i <= bars ? "on" : ""} style={{ height: 3 + i * 2.2 }} />)}
      </span>
      <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--color-muted)", fontWeight: 600, fontSize: 12 }}>{pct}%</span>
    </span>
  );
};

// ----------------------------------------------------------------- sparkline
const Sparkline = ({ data, w = 92, h = 26, color = "var(--color-primary)", fill = true }) => {
  if (!data || data.length < 2) return <svg width={w} height={h} className="spark" />;
  const max = Math.max(...data), min = Math.min(...data);
  const rng = max - min || 1;
  const pts = data.map((v, i) => [(i / (data.length - 1)) * w, h - 3 - ((v - min) / rng) * (h - 6)]);
  const path = pts.map((p, i) => (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ");
  const area = path + ` L${w} ${h} L0 ${h} Z`;
  return (
    <svg width={w} height={h} className="spark">
      {fill && <path d={area} fill={color} opacity="0.10" />}
      <path d={path} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="2.2" fill={color} />
    </svg>
  );
};

// bar chart for usage history
const UsageBars = ({ reads, h = 130, highlightLast = true }) => {
  if (!reads || reads.length < 2) return <div className="empty">Not enough reads to chart</div>;
  const items = reads.slice(1); // drop base
  const max = Math.max(...items.map((r) => r.usage), 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: h, padding: "0 2px" }}>
      {items.map((r, i) => {
        const isLast = i === items.length - 1;
        const bh = Math.max(4, (r.usage / max) * (h - 26));
        const color = r.spike ? "var(--color-danger)" : (isLast && highlightLast && r.pending) ? "var(--color-primary-alt)" : "var(--color-primary)";
        return (
          <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5, minWidth: 0 }} title={`${fmtMonth(r.date)}: ${r.usage} units`}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", fontVariantNumeric: "tabular-nums" }}>{r.usage}</span>
            <div style={{ width: "100%", maxWidth: 30, height: bh, background: color, borderRadius: "4px 4px 0 0", opacity: r.pending ? 0.85 : 1, position: "relative" }} />
            <span style={{ fontSize: 9.5, color: "var(--color-muted)" }}>{MON[r.date.getMonth()]}</span>
          </div>
        );
      })}
    </div>
  );
};

// ----------------------------------------------------------------- drawer
const Drawer = ({ open, onClose, children, wide }) => {
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <Fragment>
      <div className="scrim" onClick={onClose} />
      <aside className={`drawer ${wide ? "drawer--wide" : ""}`} role="dialog">{children}</aside>
    </Fragment>
  );
};

// modal dialog
const Modal = ({ open, onClose, title, children, width = 440 }) => {
  if (!open) return null;
  return (
    <div className="scrim" onClick={onClose} style={{ display: "grid", placeItems: "center" }}>
      <div className="card" style={{ width, maxWidth: "92vw", animation: "pop var(--duration-md)" }} onClick={(e) => e.stopPropagation()}>
        <div className="between" style={{ marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: "var(--font-size-xl)", fontWeight: 700 }}>{title}</h3>
          <button className="drawer__close" onClick={onClose}>&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
};

// detail row
const DRow = ({ k, children }) => (
  <div className="drow"><div className="drow__k">{k}</div><div className="drow__v">{children}</div></div>
);

// searchbox
const Search = ({ value, onChange, placeholder }) => (
  <div className="searchbox" style={{ minWidth: 230 }}>
    <Icon name="search" size={15} />
    <input className="input input--sm" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

// checkbox
const Checkbox = ({ state, onClick }) => (
  <button className={`cbx ${state === true ? "on" : state === "part" ? "part" : ""}`} onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}>
    {state === true && <Icon name="check" size={13} />}
    {state === "part" && <span style={{ width: 9, height: 2, background: "var(--color-primary)", borderRadius: 2 }} />}
  </button>
);

// toast host
const ToastCtx = React.createContext(() => {});
function ToastHost({ children }) {
  const [toasts, setToasts] = useState([]);
  const push = (t) => {
    const id = Math.random();
    setToasts((ts) => [...ts, { ...t, id }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), t.duration || 3800);
  };
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-wrap">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.err ? "err" : ""}`}>
            <span style={{ color: t.err ? "var(--color-danger)" : "var(--color-primary)", marginTop: 1 }}>
              <Icon name={t.err ? "alert" : "check"} size={18} />
            </span>
            <div><b>{t.title}</b>{t.body && <span>{t.body}</span>}</div>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
const useToast = () => React.useContext(ToastCtx);

// ----------------------------------------------------------------- API-down panel
// Deployed builds render this when the API can't be reached, instead of silently
// falling back to the bundled mock dataset — fake rows that look live are worse
// than an honest error. Dev builds keep the mock fallback for design work
// (screens gate on import.meta.env.DEV). `loading` renders the in-flight state:
// on a cold app start requests can take 30s+, and the mock rows must not stand
// in as the loading placeholder (they read as real data going to zero).
const ApiDown = ({ detail, loading }) => (
  <div className="page" style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
    {loading ? (
      <div style={{ textAlign: "center", color: "var(--color-text-secondary)" }}>
        <div className="muted" style={{ fontSize: 14 }}>Loading live data…</div>
        <div className="muted" style={{ fontSize: 11.5, marginTop: 6, color: "var(--color-text-tertiary)" }}>First load after a restart can take up to a minute.</div>
      </div>
    ) : (
      <div className="card" style={{ maxWidth: 440, textAlign: "center", padding: "36px 34px", background: "var(--color-surface)", border: "1px solid var(--color-border-light)" }}>
        <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, borderRadius: "50%", background: "var(--color-danger-bg)", color: "var(--color-danger)", marginBottom: 14 }}>
          <Icon name="alert" size={22} />
        </span>
        <h2 style={{ margin: "0 0 8px", fontSize: "var(--font-size-xl)", fontWeight: 700 }}>Can't reach the MSync API</h2>
        <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: "var(--color-text-secondary)" }}>
          Your data can't be loaded right now — this may be a sign-in/permission problem or an outage. Refresh to retry.
        </p>
        {detail && <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--color-muted)" }}>{detail}</div>}
      </div>
    )}
  </div>
);

export {
  fmtMoney, fmtMoney0, fmtNum, fmtHcf, fmtDate, fmtDateTime, fmtShort, fmtMonth, timeAgo, MON,
  mgrColor, MGR_COLORS, initials,
  Icon, StatusPill, Pill, Toggle, Avatar, Battery, Signal, Sparkline, UsageBars,
  Drawer, Modal, DRow, Search, Checkbox, ToastHost, useToast, ApiDown,
};
